/**
 * `fetchBaseQuery` rejects with a plain `{ status, data }` object, never an `Error`,
 * so `.message` and `String(cause)` both lose the backend's own wording.
 */
import { getErrorMessage } from 'utils/error'

export interface BackendError {
  readonly status: number
  readonly message: string
}

const readStatus = (cause: unknown): number => {
  if (cause && typeof cause === 'object' && 'status' in cause) {
    const { status } = cause as { status?: unknown }
    if (typeof status === 'number') return status
  }
  return 0
}

const readMessage = (cause: unknown): string => {
  const backendMessage = getErrorMessage(cause)
  if (typeof backendMessage === 'string' && backendMessage) return backendMessage
  // fetchBaseQuery's transport failures (FETCH_ERROR, TIMEOUT_ERROR) carry their text on `error`.
  if (cause && typeof cause === 'object' && 'error' in cause) {
    const { error } = cause as { error?: unknown }
    if (typeof error === 'string' && error) return error
  }
  if (typeof cause === 'string' && cause) return cause
  return 'The server rejected the request without giving a reason.'
}

/** Read the HTTP status and the backend's own message off a rejected query or mutation. */
export const extractBackendError = (cause: unknown): BackendError => ({
  status: readStatus(cause),
  message: readMessage(cause),
})

/** The handle `CommonExceptionHandler` stamps into every rejection message — what joins a LogRocket
 * session to a server log. Read out rather than logged whole, since the rest of the message is the
 * backend's free text and can name an account. */
export const readErrorId = (message: string): string | undefined => /\bID:\s*([A-Za-z0-9]{6,})\b/.exec(message)?.[1]
